import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useTranslation } from 'react-i18next';
import { Button, InputBar } from '~/ui';
import CustomQuerySelect, { IOption } from '~/components/Shared/CustomQuerySelect/CustomQuerySelect';
import styles from './AssetTagsBoard.module.scss';
import cn from 'classnames';
import EditIcon from '~/assets/svg/newSvg/edit.svg?react';
import BasketIcon from '~/assets/svg/newSvg/basketRaw.svg?react';
import TagIcon from '~/assets/svg/newSvg/tag-icon.svg?react';

interface Tag {
  id: number;
  name: string;
  module: string;
  asset_id: number;
  accommodations: IOption[];
}

interface AssetTagsBoardProps {
  assetId: string | undefined;
}

const AssetTagsBoard: React.FC<AssetTagsBoardProps> = ({ assetId }) => {
  const { t } = useTranslation();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [editingTagId, setEditingTagId] = useState<number | null>(null);
  const [tagName, setTagName] = useState('');
  const [selectedAccommodations, setSelectedAccommodations] = useState<IOption[]>([]);

  const { data: tags = [], isLoading } = useQuery<Tag[]>({
    queryFn: async () => {
      const { data } = await instance.get(`admin/assets/${assetId}/tags`);
      return data;
    },
    queryKey: ['asset-tags', assetId],
    enabled: !!assetId,
    onError: error => toastError(error),
  });

  const accommodationParams = {
    sort: '',
    field: '',
    asset_id: assetId,
  };

  const { mutateAsync: createTag } = useMutation(
    async () => {
      const { data } = await instance.post(`admin/assets/${assetId}/tags`, {
        name: tagName,
        accommodation_ids: selectedAccommodations.map(a => a.id),
      });
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['asset-tags', assetId]);
        resetForm();
        toastSuccess(t('tag_created'));
      },
      onError: error => toastError(error),
    }
  );

  const { mutateAsync: updateTag } = useMutation(
    async (tagId: number) => {
      const { data } = await instance.put(
        `admin/assets/${assetId}/tags/${tagId}`,
        {
          name: tagName,
          accommodation_ids: selectedAccommodations.map(a => a.id),
        }
      );
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['asset-tags', assetId]);
        resetForm();
        toastSuccess(t('tag_updated'));
      },
      onError: error => toastError(error),
    }
  );

  const { mutateAsync: deleteTag } = useMutation(
    async (tagId: number) => {
      await instance.delete(`admin/assets/${assetId}/tags/${tagId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['asset-tags', assetId]);
        toastSuccess(t('tag_deleted'));
      },
      onError: error => toastError(error),
    }
  );

  const resetForm = () => {
    setIsAdding(false);
    setEditingTagId(null);
    setTagName('');
    setSelectedAccommodations([]);
  };

  const startEdit = (tag: Tag) => {
    setEditingTagId(tag.id);
    setTagName(tag.name);
    setSelectedAccommodations(tag.accommodations);
    setIsAdding(false);
  };

  const startAdd = () => {
    setIsAdding(true);
    setEditingTagId(null);
    setTagName('');
    setSelectedAccommodations([]);
  };

  const handleSave = async () => {
    if (!tagName.trim()) return;
    if (editingTagId) {
      await updateTag(editingTagId);
    } else {
      await createTag();
    }
  };

  const renderForm = () => (
    <div className={styles.formCard}>
      <div className={styles.formInput}>
        <InputBar
          name="tagName"
          value={tagName}
          label={t('tag_name')}
          placeholder={t('tag_name_placeholder')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setTagName(e.target.value)
          }
        />
      </div>
      <div className={styles.formSelect}>
        <CustomQuerySelect
          label={t('accommodation_facilities')}
          placeholder={t('to_choose')}
          value={selectedAccommodations}
          query="admin/accommodations"
          queryKey={['tag-accommodations', accommodationParams]}
          params={accommodationParams}
          multiselect
          isSearchEnabled
          onChange={value => setSelectedAccommodations(value as IOption[])}
        />
      </div>
      <div className={styles.formActions}>
        <Button
          text={t('save')}
          onClick={handleSave}
          className={styles.saveButton}
          disabled={!tagName.trim()}
        />
        <Button
          text={t('cancel')}
          onClick={resetForm}
          className={styles.cancelButton}
          color="red"
        />
      </div>
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span className={styles.title}>{t('tags')}</span>
        {!isAdding && editingTagId === null && tags.length > 0 && (
          <Button
            text={t('add')}
            onClick={startAdd}
            className={styles.addButton}
          />
        )}
      </div>

      {/* Add form at the top */}
      {isAdding && renderForm()}

      <div className={styles.tagsList}>
        {!isLoading && tags.length === 0 && !isAdding && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <TagIcon />
            </div>
            <span className={styles.emptyTitle}>{t('no_tags')}</span>
            <span className={styles.emptySubtitle}>{t('no_tags_hint')}</span>
            <Button
              text={t('add_tag')}
              onClick={startAdd}
              className={styles.emptyAddButton}
            />
          </div>
        )}

        {tags.map(tag => (
          editingTagId === tag.id ? (
            // Inline edit form replaces the tag row
            <div key={tag.id} className={styles.inlineFormWrapper}>
              {renderForm()}
            </div>
          ) : (
            <div key={tag.id} className={styles.tagItem}>
              <div className={styles.tagLeft}>
                <div className={styles.tagBadge}>
                  <TagIcon />
                </div>
                <div className={styles.tagInfo}>
                  <span className={styles.tagName}>{tag.name}</span>
                  {tag.accommodations.length > 0 && (
                    <div className={styles.tagAccommodationCount}>
                      {tag.accommodations.map(a => (
                        <span key={a.id} className={styles.accommodationPill}>
                          {a.full_name ?? a.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.tagActions}>
                <button
                  type="button"
                  className={styles.actionButton}
                  onClick={() => startEdit(tag)}
                >
                  <EditIcon />
                </button>
                <button
                  type="button"
                  className={cn(styles.actionButton, styles.deleteButton)}
                  onClick={() => deleteTag(tag.id)}
                >
                  <BasketIcon />
                </button>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
};

export default AssetTagsBoard;
